/**
 * Behavioral configuration for split sleeper berth.
 *
 * Originally created as a temporary bridge for MYG-121231 Phase 1, which centralized
 * derivation of split sleeper berth behavioral flags so that the four Canadian rulesets
 * previously mismapped as "passenger" (Alberta, North of 60 variants) could receive
 * correct Canadian violation behavior.
 *
 * MYG-131546 migrates this toward removal: `splitType` is eventually replaced by
 * `config.isCanada` + `config.splitConfig`, at which point this file can be removed. The
 * `geometry` resolution below is the first step in that direction.
 *
 * MYG-77582 extends this file with the per-family violation behavior matrix. The matrix
 * properties below encode that matrix (rows A, B, B', D) resolved per family
 * (Canada / USA Passenger / USA Property). The calculator consumes them as: the `geometry`
 * field drives evaluator routing (`evaluateSplit` / `checkSplitPeriodsDuringWorkdayReset`),
 * while the matrix booleans carry the per-family behavior.
 *
 * The end-state behavior was originally gated per behavior by three independent `.RELEASE` flags
 * (MYG-77582 §12.5), each default OFF = before-fix behavior, ON = corrected. All three flags have
 * since been removed after 100% rollout and their corrected behavior is now permanent:
 *   - Row A, USA Passenger branch — formerly `enableUsaPassengerCanCompleteSplitInViolationFix`,
 *     made permanent by MYG-140471.
 *   - Row B, Canada — formerly `enableCanadaViolationRecalculatesWorkdayFix`, made permanent by
 *     MYG-140472.
 *   - Row B', Canada — formerly `enableCanadaViolationPreventsNewFreezeFix`, made permanent by
 *     MYG-140473.
 *
 * Before → after, by row and family (Y = true, N = false), with the gating flag named:
 *
 *   Row                                  BEFORE (flag off)        AFTER (flag on)        Gated by
 *   --------------------------------------------------------------------------------------------------------------------
 *   A  violationPreventsSplitCompletion  Can Y | Pass Y | Prop*   Can Y | Pass N | Prop*  Permanent since MYG-140471 (Pass)
 *   B  violationRecalculatesWorkday      Can N | Pass* | Prop*   Can Y | Pass* | Prop*  Permanent since MYG-140472 (Can)
 *   B' violationPreventsNewFreeze        Can N | Pass N | Prop N  Can Y | Pass N | Prop N Permanent since MYG-140473 (Can)
 *   D  failedSplitRecalculatesWorkday    all gated by unfreeze flag (unchanged by these flags)
 *
 *   * = gated by the pre-existing `enablePreventSplitCompleteWhenViolationFix`, not these flags.
 *       For USA Passenger Row B this preserves legacy behavior: the config tracks that flag exactly
 *       as pre-refactor `hosCalculator.ts` did — main computed the same
 *       isSplitBlocked = preventSplitCompleteInViolation && isSplitCompleteBlockedByViolation, and
 *       for non-Canada passenger preventSplitCompleteInViolation === the E flag. USA passenger
 *       rulesets have no workday maximum (recalc inert) since MYG-146283 removed it from the two
 *       Texas short-haul passenger rulesets (AmericaTexasShortHaul / AmericaTexasShortHaul8Day);
 *       their LEGACY configs (selected by disableTexasShortHaulRulesetCorrection) still have a 14h
 *       workdayMaximum and can be affected — identically to main (MYG-77582 Option A — legacy
 *       parity, not a behavior change).
 */

import type { IHosRulesetConfig } from "../interfaces/hosRuleset";
import type { ICustomCodeState } from "./helpers";

/** The split sleeper berth routing tier a ruleset belongs to (by geometry). */
export type TSplitGeometry = "symmetric" | "asymmetric";

export interface IIntermediateSplitConfig {
    /**
     * The split routing tier for this ruleset (symmetric vs asymmetric geometry), or
     * undefined when the ruleset has no split support. `isCanada` routes through the
     * symmetric tier by construction — even when `splitConfig` is absent (Canadian noSplit) —
     * preserving the prior family-based routing. The Canada-vs-USA-Passenger *behavior*
     * difference is NOT carried here (they share symmetric geometry); it lives in the matrix
     * booleans below, drawn via `isCanada` (MYG-77582 context §14).
     */
    geometry: TSplitGeometry | undefined;

    /**
     * Row A. When true, a Workday/Driving/Duty violation prevents the in-progress split
     * from completing. See the file-header matrix for before/after values per family.
     */
    violationPreventsSplitCompletion: boolean;

    /**
     * Row B. When true, a Workday/Driving/Duty violation occurring mid-split recalculates
     * the workday. To "recalculate the workday" means: unfreeze prior unpaired SB periods
     * (shrink workday.end back) and backdate/create any workday violations at the time they
     * would have occurred. See the file-header matrix for before/after values per family.
     */
    violationRecalculatesWorkday: boolean;

    /**
     * Row B'. When true, a driver already in a Workday/Driving/Duty violation when entering
     * SB is never granted a new workday freeze (extension) from that SB. See the file-header
     * matrix for before/after values per family.
     */
    violationPreventsNewFreeze: boolean;

    /**
     * Row D. When true, a split that fails without a violation (e.g. the driver takes a full
     * off-duty reset instead of completing the pairing) recalculates the workday. To
     * "recalculate the workday" means: unfreeze prior unpaired SB periods and backdate/create
     * any workday violations at the time they would have occurred. Gated by the unfreeze flag,
     * not affected by the per-behavior split-violation fix flags.
     */
    failedSplitRecalculatesWorkday: boolean;

    /**
     * Dispatch key that lets the Canada Row B recompute fire regardless of the unfreeze killswitch.
     * True for every Canadian ruleset (permanent since MYG-140472; formerly also required
     * `enableCanadaViolationRecalculatesWorkdayFix`).
     *
     * Row B's recompute (`recalculateWorkdayViolationsForCurrentWorkdaySymmetric`) is otherwise
     * reached only via `failedSplitRecalculatesWorkday` (= `!disableUnfreezeSBSplitNotCompleteFix`),
     * which is off in production because that killswitch is enabled everywhere — leaving Canada's Row B
     * unreachable through that path alone. `splitSb/index.ts` uses this key to give Canada its own
     * trigger so the recompute reaches customers under the killswitch. Deliberately keyed to `isCanada`
     * alone — never to `enablePreventSplitCompleteWhenViolationFix` (which is `||`-folded into
     * `violationRecalculatesWorkday`) — so this does not newly wake the pre-existing
     * prevent-split-complete customers who are dormant under the killswitch.
     */
    canadaViolationRecalcOnViolation: boolean;
}

/**
 * Resolves the split routing tier. `isCanada` always routes through the symmetric tier —
 * even for Canadian rulesets whose `splitConfig` is absent (noSplit) — which preserves the
 * prior `resolveSplitFamily` behavior where any `isCanada` ruleset resolved to "canada" and
 * dispatched to the symmetric evaluator. The Canada-vs-USA-Passenger distinction (they share
 * identical 2/8 geometry) is deliberately NOT encoded here; it is applied where it matters —
 * the matrix booleans below (via `isCanada`) and the MYG-131546 carry-forward guard in
 * `splitSb/index.ts` (via `state.config.isCanada`).
 */
function resolveSplitGeometry(config: Pick<IHosRulesetConfig, "isCanada" | "splitConfig">): TSplitGeometry | undefined {
    if (config.isCanada) {
        return "symmetric";
    }
    if (config.splitConfig?.type === "asymmetric") {
        return "asymmetric";
    }
    if (config.splitConfig?.type === "symmetric") {
        return "symmetric";
    }
    return undefined;
}

export function getIntermediateSplitConfig(
    config: Pick<IHosRulesetConfig, "isCanada" | "splitConfig">,
    customCodes: ICustomCodeState
): IIntermediateSplitConfig {
    const geometry = resolveSplitGeometry(config);
    const isCanada = config.isCanada === true;

    // The matrix booleans encode per-family behavior (Canada / USA Passenger / USA Property).
    // Canada and USA Passenger both have `geometry === "symmetric"`, so the Canada-vs-Passenger
    // distinction is drawn here via `isCanada`. `geometry === "asymmetric"` ⟺ USA Property.
    const matrix = {
        geometry,

        // Row A — Canada always blocks; USA Passenger never blocks (MYG-140471 made this permanent —
        // formerly gated by enableUsaPassengerCanCompleteSplitInViolationFix, removed after 100%
        // rollout); USA Property gated by pre-existing flag (unchanged).
        violationPreventsSplitCompletion:
            isCanada
                ? true
                : geometry === "symmetric"
                    ? false
                    : geometry === "asymmetric"
                        ? customCodes.enablePreventSplitCompleteWhenViolationFix
                        : false,

        // Row B — Canada always recalculates (permanent since MYG-140472); all others gated by
        // pre-existing flag.
        // INTENTIONAL parity change (MYG-77582, context §7.4): mismapped Canadian rulesets (Alberta /
        // North of 60) are isCanada=true, so they gate here like native Canada. `main` additionally
        // OR'd the retired `isMismappedCanadianRuleset` special-case; dropping it is deliberate
        // (mismapped rulesets are genuinely Canadian now), not a regression. This residual diff is
        // dormant in production (the recompute is U-gated).
        violationRecalculatesWorkday:
            geometry != null
                ? isCanada || customCodes.enablePreventSplitCompleteWhenViolationFix
                : false,

        // Row B' — Canada always suppresses freeze (permanent since MYG-140473); all others never
        // suppress.
        violationPreventsNewFreeze: isCanada,

        // Row D — unchanged. USA Property (asymmetric) always recalculates on a failed split and is
        // deliberately NOT gated by the unfreeze killswitch: on `main` the property path ran
        // unconditionally (guarded only by !completedSplitSB), while only the passenger/canada
        // (symmetric) path was killswitch-gated (useUnfreezeFix). Hardcoding `true` preserves that parity.
        failedSplitRecalculatesWorkday:
            geometry === "asymmetric"
                ? true
                : geometry != null
                    ? !customCodes.disableUnfreezeSBSplitNotCompleteFix
                    : false,

        // Dispatch key that lets the Canada Row B recompute fire regardless of the unfreeze
        // killswitch. Keyed to isCanada alone (never enablePreventSplitCompleteWhenViolationFix)
        // so it does not wake E customers who are dormant under the killswitch. See index.ts.
        canadaViolationRecalcOnViolation: isCanada
    };

    return matrix;
}
